import React from 'react';
import { Table, Tag, Progress, Button, Alert, TablePaginationConfig } from 'antd';
import { DealListDto, DealStage } from 'api/models';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import { formatCurrency, formatDate } from 'utility/utility';
import { ColumnsType } from 'antd/lib/table';
import styled from 'styled-components';
import { Cards } from 'components/cards/frame/cards-frame';
import { CustomTableStyleWrapper } from 'pages/style';
import { TableWrapper } from 'container/styled';
import { getStageColor, getStageName } from '../constants';
import dayjs from 'dayjs';
interface DealsTableProps {
  deals: DealListDto[];
  loading: boolean;
  onRefresh: () => void;
  onDelete: (id: number) => void;
  pagination: TablePaginationConfig;
}

const CashflowsProgress = styled(Progress)`
  .ant-progress-outer {
    width: 33%;
  }
`;

const DealsTable: React.FC<DealsTableProps> = ({ deals, loading, onRefresh, onDelete, pagination }) => {
  const { t } = useTranslation();

  const columns: ColumnsType<DealListDto> = [
    {
      title: t('deals:client', 'Client'),
      dataIndex: 'clientName',
      key: 'clientName',
      render: (text: string, record: DealListDto) => <Link to={`/deals/${record.id}`}>{text}</Link>,
    },
    {
      title: t('deals:project', 'Project'),
      dataIndex: 'projectName',
      key: 'projectName',
    },
    {
      title: t('deals:units', 'Units'),
      dataIndex: 'unitsCount',
      key: 'unitsCount',
      align: 'center' as const,
    },
    {
      title: t('deals:total-amount', 'Total Amount'),
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount: number) => formatCurrency(amount),
    },
    {
      title: t('deals:payment-status', 'Payment Status'),
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (paidAmount: number, record: DealListDto) => {
        const percentage = (paidAmount / record.totalAmount!) * 100;
        return (
          <div style={{ minWidth: 200 }}>
            <CashflowsProgress
              percent={Math.round(percentage)}
              size="small"
              format={() => `${formatCurrency(paidAmount)} / ${formatCurrency(record.totalAmount)}`}
              status={percentage === 100 ? 'success' : 'active'}
            />
            {record.stage === DealStage.InProgress && (
              <div className="text-xs mt-1">
                {record.nextPaymentDue && record.nextPaymentDue !== '0001-01-01T00:00:00' ? (
                  <>
                    <FeatherIcon icon="calendar" size={12} className="mr-1" />
                    {t('deals:next-payment', 'Next Payment')}: {dayjs(record.nextPaymentDue).format('YYYY-MM-DD')} (
                    {formatCurrency(record.nextPaymentAmount)})
                  </>
                ) : !paidAmount || paidAmount === 0 ? (
                  <Alert
                    message={t('deals:no-payment-schedule', 'No payment schedule set')}
                    type="warning"
                    showIcon
                    style={{ padding: '2px 8px', fontSize: '13px' }}
                  />
                ) : null}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('deals:stage', 'Stage'),
      dataIndex: 'stage',
      key: 'stage',
      render: (stage: DealStage) => (
        <Tag color={getStageColor(stage)}>{t(getStageName(stage)?.key!, getStageName(stage)?.title!)}</Tag>
      ),
    },
    {
      title: t('deals:status', 'Status'),
      dataIndex: 'closedAt',
      key: 'closedAt',
      render: (closedAt: string | null) => (
        <span className={`status-text ${closedAt ? 'deactivate' : 'active'}`}>
          {closedAt ? t('global:closed', 'Closed') : t('global:active', 'Active')}
        </span>
      ),
    },
    {
      title: t('deals:created', 'Created'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'actions',
      render: (record: DealListDto) => (
        <div className="table-actions">
          <Link to={`/deals/${record.id}`}>
            <Button className="btn-icon" type="default" shape="circle">
              <FeatherIcon icon="eye" size={16} />
            </Button>
          </Link>
          {record.isActive && !record.closedAt && (!record.paidAmount || record.paidAmount === 0) && (
            <>
              <Link to={`/deals/${record.id}/edit`}>
                <Button className="btn-icon" type="primary" shape="circle">
                  <FeatherIcon icon="edit" size={16} />
                </Button>
              </Link>
              <Button
                type="text"
                danger
                icon={<FeatherIcon icon="trash-2" size={16} />}
                onClick={e => {
                  e.stopPropagation();
                  onDelete?.(record.id!);
                }}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <Cards headless>
      <CustomTableStyleWrapper>
        <TableWrapper>
          <Table
            dataSource={deals}
            columns={columns}
            rowKey="id"
            loading={loading}
            scroll={{ x: 1200 }}
            pagination={pagination}
          />
        </TableWrapper>
      </CustomTableStyleWrapper>
    </Cards>
  );
};

export default DealsTable;
